import React, { useEffect, useState } from 'react';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Dialog } from '@mui/material';
import { functions } from '../../firebase';
import { Card } from '../today/cards';
import { PaperBlock } from '../../components/buyerForm/BuyerFormApp';
import { DOCS, OCCUPATIONS, fullName, type Buyer, type DocsByBuyer } from '../../components/buyerForm/buyerFormModel';
import '../../components/buyerForm/buyerForm.css';

type FormDoc = {
  status?: 'draft' | 'submitted' | 'approved';
  buyers?: Buyer[];
  docs?: DocsByBuyer;
  submittedVia?: 'link' | 'office';
  submittedAt?: any; approvedAt?: any; approvedBy?: string;
  link?: { phoneHint?: string; sentToName?: string | null; sentAt?: any; expiresAt?: any; openedAt?: any; revoked?: boolean };
};

const callInvite = httpsCallable<any, any>(functions, 'buyerFormInvite');
const callReview = httpsCallable<any, any>(functions, 'buyerFormReview');
const ms = (v: any) => (typeof v?.toMillis === 'function' ? v.toMillis() : 0);
const day = (v: any) => (ms(v) ? new Date(ms(v)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '');
const errText = (e: any) => String(e?.message || 'Something went wrong.').replace(/^Firebase(Error)?:\s*/i, '');
const RELATION: Record<string, string> = { son: 'Son of', daughter: 'Daughter of', wife: 'Wife of' };

/** Unit page: send the buyer details link, open the form at the office, and approve what buyers send. */
export function BuyerFormCard({ projectId, unitId, bookingId, defaultName, defaultMobile, onFillHere }: {
  projectId: string; unitId: string; bookingId: string;
  defaultName: string; defaultMobile: string;
  onFillHere: () => void;
}) {
  const [form, setForm] = useState<FormDoc | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => onSnapshot(
    doc(getFirestore(), 'projects', projectId, 'units', unitId, 'bookings', bookingId, 'buyerForm', 'current'),
    (s) => setForm(s.exists() ? (s.data() as FormDoc) : null),
    () => setForm(null)
  ), [bookingId, projectId, unitId]);

  const place = { projectId, unitId, bookingId };
  const review = async (decision: 'approve' | 'reopen' | 'revoke') => {
    setBusy(decision);
    setMsg('');
    try {
      await callReview({ ...place, decision });
      if (decision === 'approve') setReviewOpen(false);
      setMsg(decision === 'approve' ? 'Approved. Buyer details and KYC are saved.' : decision === 'reopen' ? 'Reopened. The buyer can change the details again.' : 'Link stopped.');
    } catch (e) {
      setMsg(errText(e));
    } finally {
      setBusy('');
    }
  };

  const status = form?.status || (form?.link ? 'draft' : '');
  const linkLive = !!form?.link && !form.link.revoked && ms(form.link.expiresAt) > Date.now();
  const line = (() => {
    if (status === 'approved') return `Approved ${day(form?.approvedAt)}${form?.submittedVia === 'office' ? ', filled at the office' : ''}`;
    if (status === 'submitted') return `Sent by ${form?.submittedVia === 'office' ? 'the office' : 'the buyer'} ${day(form?.submittedAt)}. Waiting for your review.`;
    if (linkLive) return `Link sent ${day(form?.link?.sentAt)} to ${form?.link?.sentToName || 'buyer'} (${form?.link?.phoneHint})${form?.link?.openedAt ? `, opened ${day(form.link.openedAt)}` : ', not opened yet'}`;
    if (form?.link) return form.link.revoked ? 'The link was stopped.' : 'The link has expired.';
    return 'Buyers fill their own details in English and check the Gujarati.';
  })();
  const chip = status === 'approved' ? ['good', 'Approved'] : status === 'submitted' ? ['warn', 'To review'] : linkLive ? ['acc', form?.link?.openedAt ? 'Opened' : 'Link sent'] : ['mute', 'Not sent'];

  return (
    <Card index={9} label="Buyer details form">
      <div className="ot-hd">
        <div><h3>Buyer details form</h3><p>{line}</p></div>
        <span className={`ot-chip c-${chip[0]}`}>{chip[1]}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {status === 'submitted' ? <button className="ot-btn sm dark" onClick={() => setReviewOpen(true)}>Review details</button> : null}
        {status !== 'submitted' ? <button className="ot-btn sm dark" onClick={() => setSendOpen(true)}>{linkLive ? 'Send again' : 'Send on WhatsApp'}</button> : null}
        {status !== 'submitted' ? <button className="ot-btn sm" onClick={onFillHere}>Fill with buyer here</button> : null}
        {status === 'submitted' || status === 'approved' ? <button className="ot-btn sm" onClick={() => review('reopen')} disabled={!!busy}>{busy === 'reopen' ? 'Reopening…' : 'Reopen for changes'}</button> : null}
        {linkLive && status === 'draft' ? <button className="ou-linkbtn danger" onClick={() => review('revoke')} disabled={!!busy}>{busy === 'revoke' ? 'Stopping…' : 'Stop link'}</button> : null}
      </div>
      {msg ? <p className="faint" role="status" style={{ margin: '8px 0 0', fontSize: 12.5 }}>{msg}</p> : null}

      <SendDialog open={sendOpen} onClose={() => setSendOpen(false)} place={place} defaultName={defaultName} defaultMobile={defaultMobile} />

      <Dialog open={reviewOpen} onClose={() => !busy && setReviewOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: '18px' } }}>
        <div className="ot" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0, borderRadius: 0, background: 'var(--ot-surface)' }}>
          <div className="ot-hd" style={{ margin: 0 }}>
            <div><h3>Review buyer details</h3><p>{form?.buyers?.length || 0} buyer{(form?.buyers?.length || 0) === 1 ? '' : 's'} · sent {day(form?.submittedAt)} {form?.submittedVia === 'office' ? 'at the office' : 'by the buyer'}</p></div>
          </div>
          {(form?.buyers || []).map((b, i) => {
            const files = form?.docs?.[b.key] || {};
            return (
              <div key={b.key} style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: i ? 14 : 0, borderTop: i ? '1px solid var(--ot-line)' : 0 }}>
                <b style={{ fontSize: 15 }}>Buyer {i + 1}: {fullName(b)}{i > 0 && b.relationToFirst ? <span className="faint" style={{ fontWeight: 400 }}> · {b.relationToFirst} of buyer 1</span> : null}</b>
                <PaperBlock b={b} index={i} />
                <div className="ot-kvs" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '4px 16px', fontSize: 13 }}>
                  <span><span className="muted">{RELATION[b.fatherRelation] || 'Father / husband'}:</span> {b.fatherName} <span className="gu">({b.gu.fatherName})</span></span>
                  <span><span className="muted">Born:</span> {b.dateOfBirth} · {b.gender}</span>
                  <span><span className="muted">Occupation:</span> {b.occupation === 'Other' ? b.occupationOther : OCCUPATIONS.find((o) => o.value === b.occupation)?.label || b.occupation}</span>
                  <span><span className="muted">Living in:</span> {b.residentStatus === 'nri' ? 'Outside India (NRI)' : 'India'}</span>
                  <span><span className="muted">Aadhaar:</span> {b.aadhaar ? `•••• •••• ${b.aadhaar.slice(-4)}` : '-'}</span>
                  <span><span className="muted">WhatsApp:</span> {b.whatsappSame ? 'same as mobile' : b.whatsapp || '-'}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {DOCS.map((d) => (files[d.key]
                    ? <a key={d.key} className="ot-btn sm" href={files[d.key]!.url} target="_blank" rel="noopener noreferrer">{d.label}</a>
                    : <span key={d.key} className="ot-chip c-mute">{d.label}: not uploaded</span>))}
                </div>
              </div>
            );
          })}
          <p className="faint" style={{ margin: 0, fontSize: 12.5 }}>Approving saves these buyers on the booking (updating existing ones), with PAN and Aadhaar numbers and the uploaded copies in KYC. Buyers already on the booking but not in this list are left as they are.</p>
          {msg && reviewOpen ? <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--ot-bad-ink)' }}>{msg}</p> : null}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button className="ot-btn" onClick={() => setReviewOpen(false)} disabled={!!busy}>Close</button>
            <button className="ot-btn" onClick={() => review('reopen')} disabled={!!busy}>{busy === 'reopen' ? 'Reopening…' : 'Send back for changes'}</button>
            <button className="ot-btn dark" onClick={() => review('approve')} disabled={!!busy}>{busy === 'approve' ? 'Saving…' : 'Approve and save'}</button>
          </div>
        </div>
      </Dialog>
    </Card>
  );
}

function SendDialog({ open, onClose, place, defaultName, defaultMobile }: {
  open: boolean; onClose: () => void; place: { projectId: string; unitId: string; bookingId: string };
  defaultName: string; defaultMobile: string;
}) {
  const [name, setName] = useState(defaultName);
  const [phone, setPhone] = useState(defaultMobile);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ link: string; manualUrl: string } | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!open) return;
    setName(defaultName);
    setPhone(String(defaultMobile || '').replace(/\D/g, '').slice(-10));
    setError('');
    setResult(null);
    setCopied(false);
  }, [defaultMobile, defaultName, open]);
  const create = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await callInvite({ ...place, phone, name });
      setResult(res.data);
      window.open(res.data.manualUrl, '_blank', 'noopener');
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '18px' } }}>
      <div className="ot" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0, borderRadius: 0, background: 'var(--ot-surface)' }}>
        <div className="ot-hd" style={{ margin: 0 }}><div><h3>Send buyer details form</h3><p>Opens WhatsApp with the message ready. Any older link stops working.</p></div></div>
        {result ? (
          <>
            <p style={{ margin: 0, fontSize: 13.5 }}>WhatsApp should have opened with the message. If it did not, use one of these:</p>
            <a className="ot-btn dark" href={result.manualUrl} target="_blank" rel="noopener noreferrer">Open WhatsApp again</a>
            <button className="ot-btn" onClick={() => { navigator.clipboard?.writeText(result.link).then(() => setCopied(true)).catch(() => setCopied(false)); }}>{copied ? 'Link copied' : 'Copy link'}</button>
            <p className="faint" style={{ margin: 0, fontSize: 12 }}>The link works for 7 days, until the buyer sends the form.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="ot-btn" onClick={onClose}>Done</button></div>
          </>
        ) : (
          <>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5 }} className="muted">
              Name in the message
              <input value={name} onChange={(e) => setName(e.target.value)} style={{ font: 'inherit', fontSize: 15, padding: '10px 12px', borderRadius: 12, border: '1px solid var(--ot-line)', background: 'var(--ot-surface)', color: 'var(--ot-ink)' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5 }} className="muted">
              WhatsApp number (buyer or a family member)
              <input inputMode="tel" maxLength={10} value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(-10))} style={{ font: 'inherit', fontSize: 15, padding: '10px 12px', borderRadius: 12, border: '1px solid var(--ot-line)', background: 'var(--ot-surface)', color: 'var(--ot-ink)', fontVariantNumeric: 'tabular-nums' }} />
            </label>
            {error ? <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--ot-bad-ink)' }}>{error}</p> : null}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="ot-btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="ot-btn dark" onClick={create} disabled={busy || phone.length !== 10}>{busy ? 'Making link…' : 'Open WhatsApp'}</button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
